import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  forceLogoutAdminUser,
  listAdminUsers,
  setAdminOrgStatus,
  setAdminUserStatus,
  updateAdminUserRole,
} from '../../lib/adminApi';
import type { AccountStatus, AdminUsersFilters, OrgStatus } from '../../types/admin';
import type { GlobalRole } from '../../types';
import { adminKeys } from './adminQueryKeys';

export function useAdminUsers(filters: AdminUsersFilters) {
  return useQuery({
    queryKey: adminKeys.users.list(filters as Record<string, unknown>),
    queryFn: () => listAdminUsers(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: GlobalRole }) => updateAdminUserRole(userId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users.all });
    },
  });
}

export function useSetUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status, reason }: { userId: string; status: AccountStatus; reason?: string }) =>
      setAdminUserStatus(userId, { status, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users.all });
      void queryClient.invalidateQueries({ queryKey: adminKeys.growth.all });
    },
  });
}

export function useForceLogoutUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => forceLogoutAdminUser(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users.all });
    },
  });
}

export function useSetOrgStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, status, reason }: { organizationId: string; status: OrgStatus; reason?: string }) =>
      setAdminOrgStatus(organizationId, { status, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.growth.all });
      void queryClient.invalidateQueries({ queryKey: adminKeys.users.all });
    },
  });
}
